import Constants from '@/Constants';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import LoaderSpinner from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import './style.scss';

export enum ButtonStyle {
    PromptButtonStyle = 'prompt',
    ActionButtonStyle = 'action',
    NoButtonStyle = 'noButton',
}

type MouseEventHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

interface Props {
    modalId: string;

    loading?: boolean;
    success: boolean;
    buttonStyle: ButtonStyle;

    icon?: string;
    title?: string;
    message?: string;
    mainBtnLabel?: string;

    onCloseModal?: CallableFunction;
    onDismiss?: CallableFunction;
    onSuccess?: CallableFunction;
    onRetry?: MouseEventHandler;
    onYes?: MouseEventHandler;
    onNo?: MouseEventHandler;
}

type Locale = { strings: LocaleData };

type ModalModeProps = Props & Locale;

const noop = () => {};

const Prompt = (props: ModalModeProps) => {
    const { strings } = props;

    const handleYes = (props.onYes || noop) as MouseEventHandler;
    const handleNo = (props.onNo || noop) as MouseEventHandler;

    return (
        <>
            <button className="btn btn-light" onClick={handleNo}>
                {strings.NO}
            </button>
            <button className="btn btn-primary" onClick={handleYes}>
                {strings.YES}
            </button>
        </>
    );
};

const Retry = (props: ModalModeProps) => {
    const { strings } = props;
    const handleRetry = (props.onRetry || noop) as MouseEventHandler;

    return (
        <button className="btn btn-primary" onClick={handleRetry}>
            {props.mainBtnLabel || strings.TRY_AGAIN}
        </button>
    );
};

const Loader = () => {
    const { colors } = useSelector((state: ApplicationState) => state.appOrganization.data);
    return (
        <div className="loader-container">
            <LoaderSpinner color={colors[0]} height={40} type="Oval" width={40} />
        </div>
    );
};

const ActionFeedbackModalBody: React.FC<Props> = (props: Props) => {
    const { success, buttonStyle, modalId, loading } = props;

    const { strings } = useSelector((state: ApplicationState) => ({ strings: state.locale.data }));
    const [modal, setModal] = useState<HTMLElement>();

    const isPrompt = buttonStyle === ButtonStyle.PromptButtonStyle;
    const closable = !success && !isPrompt;

    const icon = props.icon || (success ? Constants.icons.CHECK_CIRCLE : Constants.icons.REPORT_PROBLEM);
    const title = props.title || (success ? strings.SUCCESS_WITH_EXCLAMATION : strings.OPS);
    const message = props.message || (success ? '' : strings.ERROR_WHILE_SAVE_DATA);

    const onDismiss = props.onDismiss || noop;
    const onSuccess = props.onSuccess || noop;
    const onCloseModal = props.onCloseModal;

    const doSuccessAnimation = () => {
        if (modal) {
            modal.classList.add('animation');
            setTimeout(() => {
                modal.classList.add('animation--opacity');
                setTimeout(() => {
                    onDismiss();
                    onSuccess();
                }, 2000);
            }, 500);
        }
    };

    useEffect(() => {
        if (success && modal && buttonStyle === ButtonStyle.NoButtonStyle) {
            setTimeout(doSuccessAnimation, 500);
        }
    }, [success, modal, buttonStyle]);

    useEffect(() => {
        if (modalId) {
            const modal = document.getElementById(modalId) as HTMLElement;
            modal.classList.add('feedbackModal');

            if (buttonStyle === ButtonStyle.NoButtonStyle) {
                modal.classList.add('feedbackModal--simple');
            } else {
                modal.classList.remove('feedbackModal--simple');
            }

            setModal(modal);
        }
    }, [modalId, buttonStyle]);

    useEffect(() => {
        return () => {
            const modal = document.getElementById(modalId) as HTMLElement;
            modal.classList.remove('feedbackModal');

            if (buttonStyle === ButtonStyle.NoButtonStyle) {
                modal.classList.remove('feedbackModal--simple');
            }

            setModal(modal);
        };
    }, []);

    const Buttons = isPrompt ? Prompt : Retry;

    return (
        <>
            {buttonStyle === ButtonStyle.NoButtonStyle ? (
                <Modal.Body>
                    {(loading && <Loader />) || (
                        <div className="feedback-container">
                            <i className={`material-icons-outlined ${success ? 'success' : ''}`}>{icon}</i>
                            <p>{message}</p>
                        </div>
                    )}
                </Modal.Body>
            ) : (
                <>
                    {!loading && closable && !onCloseModal && <Modal.Header closeButton />}
                    <Modal.Body>
                        <div className="containerModal">
                            {onCloseModal && (
                                <i className="material-icons-outlined close-icon" onClick={() => onCloseModal()}>
                                    {Constants.icons.CLOSE}
                                </i>
                            )}
                            <div className="feedback-container">
                                {(loading && <Loader />) || (
                                    <>
                                        <i className={`material-icons-outlined ${success ? 'success' : ''}`}>{icon}</i>
                                        <h1>{title}</h1>
                                        <p>{message}</p>
                                        <div className="btn-container">
                                            <Buttons strings={strings} {...props} />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </Modal.Body>
                </>
            )}
        </>
    );
};

export default ActionFeedbackModalBody;
